// BasisOfCost.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// FIX: this used to import the plain npm bootstrap.min.css directly,
// which webpack injects AFTER the theme's own compiled CSS <link> tags in
// public/index.html -- same load-order bug already fixed in src/index.js
// and Step5Final.jsx, silently overriding the theme's styling here too.
import { API_ORIGIN, getAuthToken, api, authFetch } from "../config/apiConfig";
import { ROUTES } from "../config/routes";
import { printProjectReport } from "../utils/printProjectReport";

// FIX: was hardcoded to "/api" -- every other page
// derives this from API_ORIGIN (src/config/apiConfig.js).
const API_BASE = API_ORIGIN;
const ELEMENT_SCOPE_ROUTE = ROUTES.ELEMENT_SCOPE_PAGE1; // target route for Next

// Same wizard-step indicator as every other Cost Estimation page.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "basis";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// Read-only view: every step tile is clickable and jumps straight to that
// step's own read-only view page (this project is already "done", so
// there's no notion of a single "current" step here).
const STEP_VIEW_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW,
};

function makePreview(file) {
  return {
    id: Math.random().toString(36).slice(2, 9),
    file,
    name: file.name,
    size: file.size,
    type: file.type,
    url: URL.createObjectURL(file)
  };
}

export default function BasisOfCostReadOnly() {
  const navigate = useNavigate();
  const fileInputRefs = useRef({});

  // Jumps to another step's read-only view page. The project id this page
  // relies on (see the DB-prefill effect below) lives in localStorage and
  // is left untouched, so the next read-only page picks up the same
  // project automatically.
  const handleStepNav = (stepKey) => {
    const route = STEP_VIEW_ROUTES[stepKey];
    if (route) navigate(route);
  };

  const [rows, setRows] = useState([{ drawingName: "", drawingNo: "", url: "", attached: null }]);
  const [assumptions, setAssumptions] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [savedBasis, setSavedBasis] = useState(null);

  // Same "Project : <name>" white-block header as Step2RiskInflation.jsx /
  // Step3AssetSelection.jsx / Step4SubElement.jsx / Step5Final.jsx -- reads
  // the flat "projectName" key Step1ProjectInfo.jsx saves (not the "step3"
  // wizard-draft JSON those later steps use, since Basis of Cost doesn't
  // otherwise load that draft).
  const [projectName, setProjectName] = useState("");
  useEffect(() => {
    setProjectName(localStorage.getItem("projectName") || "");
  }, []);

  // FIX: this page previously had no way to read back a project's
  // already-saved Basis of Cost (Drawing Name/No., URL, Assumptions,
  // Exclusions) -- it only ever loaded the local "basis_of_cost_draft"
  // cache, which is a leftover from the EDITABLE BasisOfCost.jsx wizard
  // page (used to survive an accidental reload mid-edit) and is not
  // scoped per project. Since this is a read-only VIEW page, not an
  // editor, it has no business trusting that cache at all: once any
  // project's Basis of Cost had ever been opened (even here), that one
  // global localStorage key would silently "stick" and every other
  // project's read-only view would show that stale/empty draft instead
  // of its own real saved data -- which is exactly the bug reported
  // ("fields are empty" when re-opening a different project). Fixed by
  // removing the draft short-circuit entirely: this page now always
  // fetches the real record straight from the server. GET
  // /basis_of_cost_api (same route as the existing POST save -- see
  // app.py) returns that saved record.
  useEffect(() => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`${API_BASE}/basis_of_cost_api?Projectid=${encodeURIComponent(projectId)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = await res.json();
        if (!data || !data.success || !data.found) return;
        const loadedRows = (Array.isArray(data.items) && data.items.length
          ? data.items
          : [{ drawingName: "", drawingNo: "", url: "" }]
        ).map((it) => ({
          drawingName: it.drawingName || it.DrawingName || "",
          drawingNo: it.drawingNo || it.DrawingNo || "",
          url: it.url || "",
          attached: null
        }));

        // Match each previously-uploaded document (dbo.Basis_CostFiles,
        // returned above as data.files) back onto its row by (Drawing
        // Name, Drawing No.) -- the same pair the POST save wrote
        // alongside that file. Per request: the "Upload (jpg/pdf)" field
        // was still showing blank for a row whose document was already
        // uploaded, even though the file is sitting on disk under
        // Document/<CET_ID>/ and listed in Basis_CostFiles -- this is
        // what fills that field back in. Each file is spliced out of
        // remainingFiles once matched so two files can't both attach to
        // the same row.
        if (Array.isArray(data.files) && data.files.length) {
          const remainingFiles = [...data.files];
          loadedRows.forEach((row) => {
            const idx = remainingFiles.findIndex(
              (f) =>
                (f.drawingName || "").trim() === (row.drawingName || "").trim() &&
                (f.drawingNo || "").trim() === (row.drawingNo || "").trim()
            );
            if (idx !== -1) {
              const f = remainingFiles.splice(idx, 1)[0];
              // No blob/local URL here (existing:true) -- it's a file
              // already saved on the server, not one picked in this
              // session, so it's fetched and opened on demand instead
              // (see viewExistingBasisFile / the Upload column below).
              row.attached = {
                name: f.filename,
                existing: true,
                basisId: data.basis_id,
                filename: f.filename
              };
            }
          });
        }

        setRows(loadedRows);
        setAssumptions(data.assumptions || "");
        setExclusions(data.exclusions || "");
      } catch (err) {
        console.error("Failed to load saved basis of cost for prefill:", err);
      }
    })();
  }, []);

  // FIX: this used to unconditionally re-serialize whatever was on screen
  // back into that same global "basis_of_cost_draft" key on every change
  // -- which is exactly what caused the very first project viewed here to
  // "poison" the localStorage cache for every project viewed afterwards
  // (see the DB-prefill effect above). This is a read-only view; it has
  // nothing to persist and no reason to touch that key at all.

  const addRow = () => setRows(prev => [...prev, { drawingName: "", drawingNo: "", url: "", attached: null }]);
  const removeRow = (idx) => {
    setRows(prev => {
      const copy = [...prev];
      const removed = copy.splice(idx, 1)[0];
      if (removed && removed.attached && removed.attached.url) URL.revokeObjectURL(removed.attached.url);
      return copy.length ? copy : [{ drawingName: "", drawingNo: "", url: "", attached: null }];
    });
  };
  const updateRow = (idx, field, value) => setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  // FIX: Drawing Name / Drawing No. had no character limit, so they could
  // be typed longer than the database columns that store them
  // (DrawingName nvarchar(255), DrawingNo nvarchar(100), per the schema)
  // -- risking a truncated/rejected save on the backend. maxLength on the
  // inputs below stops typing past the limit; this extra check also
  // catches a paste that drops in more than the limit at once and tells
  // the user why it was capped.
  const DRAWING_NAME_MAX = 255;
  const DRAWING_NO_MAX = 100;

  const updateRowWithLimit = (idx, field, value, max) => {
    if (value.length > max) {
      alert(`Maximum ${max} characters allowed.`);
      updateRow(idx, field, value.slice(0, max));
      return;
    }
    updateRow(idx, field, value);
  };

  // FIX: per request, Drawing Name can't be left blank when a document is
  // uploaded -- without it, buildUploadFilename()/buildFilesMeta() would
  // still send the file, just with no way to identify which drawing it
  // belongs to. Checked here (blocking the attach itself) rather than
  // only at Save/Next time, so the user finds out immediately instead of
  // after picking a file.
  const onRowFileSelected = (idx, file) => {
    if (!file) return;
    const currentName = (rows[idx] && rows[idx].drawingName) || "";
    if (!currentName.trim()) {
      alert("Please enter a Drawing Name before uploading a document.");
      return;
    }
    const preview = makePreview(file);
    preview.file = file;
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, attached: preview } : r)));
  };

  const clearDraft = () => {
    rows.forEach(r => { if (r.attached && r.attached.url) URL.revokeObjectURL(r.attached.url); });
    setRows([{ drawingName: "", drawingNo: "", url: "", attached: null }]);
    setAssumptions("");
    setExclusions("");
    try { localStorage.removeItem("basis_of_cost_draft"); } catch {}
    setInfo(null);
    setError(null);
    setSavedBasis(null);
    setShowSuccessAlert(false);
  };

  // FIX: uploaded drawing files kept whatever name was on the user's own
  // machine (e.g. "scan0001.pdf"), which meant nothing once several
  // projects' drawings landed in the same server folder -- no way to tell
  // whose file was whose. Per request, rename every newly-attached file to
  // "<ProjectName>_<originalFileName>_<row number>" (1 for the first
  // drawing row, 2 for the second, ...) before it's sent, so the stored
  // filename is traceable back to the project on its own. Project name
  // and the original name are both sanitized to safe filename characters
  // first; row number matches the row's own position (the same "Drawing
  // 1" the first row's placeholder implies), not a count of only the rows
  // that have a file, so it stays stable even if an earlier row is left
  // empty.
  const sanitizeForFilename = (s) =>
    (s || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  const buildUploadFilename = (file, rowIndex) => {
    const dot = file.name.lastIndexOf(".");
    const base = dot > 0 ? file.name.slice(0, dot) : file.name;
    const ext = dot > 0 ? file.name.slice(dot) : "";
    const projectPart = sanitizeForFilename(projectName) || "Project";
    const namePart = sanitizeForFilename(base) || "file";
    return `${projectPart}_${namePart}_${rowIndex + 1}${ext}`;
  };

  const buildFilesMeta = () => {
    const filesMeta = {};
    rows.forEach((r, idx) => {
      if (r.attached && r.attached.file) {
        filesMeta[buildUploadFilename(r.attached.file, idx)] = {
          drawingName: r.drawingName || null,
          drawingNo: r.drawingNo || null
        };
      } else if (r.url) {
        try {
          const candidate = r.url.split("/").pop();
          if (candidate) {
            filesMeta[candidate] = {
              drawingName: r.drawingName || null,
              drawingNo: r.drawingNo || null
            };
          }
        } catch {}
      }
    });
    return filesMeta;
  };

  // Upload helper: saves to server and returns server response
  const uploadToServer = async (navigateAfter = false, nextRoute = ELEMENT_SCOPE_ROUTE) => {
    setError(null);
    setInfo(null);

    const projectId = localStorage.getItem("projectID");
    if (!projectId) {
      setError("Missing projectID in localStorage. Please complete Step 1.");
      return { ok: false };
    }

    const hasDrawing = rows.some(r => r.drawingName || r.drawingNo || r.url || r.attached);
    if (!hasDrawing && !assumptions.trim() && !exclusions.trim()) {
      setError("Please add at least one drawing, or enter assumptions/exclusions before saving.");
      return { ok: false };
    }

    // Safety net for the same "Drawing Name required with an upload" rule
    // enforced in onRowFileSelected above -- catches a row whose name was
    // cleared out again after the file was already attached.
    const missingNameIdx = rows.findIndex(r => r.attached && !(r.drawingName || "").trim());
    if (missingNameIdx !== -1) {
      setError(`Please enter a Drawing Name for the uploaded document in row ${missingNameIdx + 1}.`);
      return { ok: false };
    }

    const form = new FormData();
    form.append("Projectid", projectId);
    const items = rows.map(r => ({ drawingName: r.drawingName, drawingNo: r.drawingNo, url: r.url }));
    form.append("items", JSON.stringify(items));
    form.append("assumptions", assumptions || "");
    form.append("exclusions", exclusions || "");

    const filesMeta = buildFilesMeta();
    form.append("files_meta", JSON.stringify(filesMeta));

    rows.forEach((r, idx) => {
      if (r.attached && r.attached.file) {
        form.append("files", r.attached.file, buildUploadFilename(r.attached.file, idx));
      }
    });

    setUploading(true);
    setProgress(0);

    try {
      const resJson = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_BASE}/basis_of_cost_api`, true);
        // /basis_of_cost_api now requires a JWT like every other route
        // (see @jwt_required in app.py) -- this is a raw XMLHttpRequest
        // (not fetch/axios, needed for the upload-progress event below),
        // so apiConfig.js's authFetch()/api wrapper doesn't cover it;
        // the header has to be attached by hand here instead.
        const _token = getAuthToken();
        if (_token) xhr.setRequestHeader("Authorization", `Bearer ${_token}`);

        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            const percent = Math.round((evt.loaded / evt.total) * 100);
            setProgress(percent);
          }
        };

        xhr.onload = () => {
          const text = xhr.responseText || "";
          let json = null;
          try {
            json = JSON.parse(text);
          } catch {
            json = { success: xhr.status >= 200 && xhr.status < 300, message: text };
          }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(json);
          } else {
            reject(new Error(json.error || json.message || `Server responded ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.onabort = () => reject(new Error("Upload aborted"));

        xhr.send(form);
      });

      // Save successful: keep form values (do not clear), show success alert and store savedBasis
      const serverMessage = resJson.message || "Data inserted successfully";
      setInfo(serverMessage);
      setSavedBasis(resJson || { success: true, message: serverMessage, basis_id: null, files: [] });

      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3500);

      // keep form values intact so user can continue editing
      // optionally remove draft from localStorage if you want server state canonical
      try { localStorage.removeItem("basis_of_cost_draft"); } catch {}

      if (navigateAfter) {
        // ensure projectID is present for the next page
        localStorage.setItem("projectID", projectId);
        navigate(nextRoute);
      }

      return { ok: true, data: resJson };
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to save basis of cost");
      return { ok: false, error: err };
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Navigate helper: ensures projectID exists and navigates to Element Scope page
  const navigateToElementScope = () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) {
      setError("Missing projectID. Cannot open Element Construction Scope.");
      return;
    }
    // store projectID again to be safe
    localStorage.setItem("projectID", projectId);
    navigate(ELEMENT_SCOPE_ROUTE);
  };

  // Next: save first (if needed) then navigate to Element Scope page
  const handleNext = async () => {
    // If already saved and server returned success, navigate immediately
    if (savedBasis && savedBasis.success) {
      navigateToElementScope();
      return;
    }

    // Otherwise save then navigate
    const res = await uploadToServer(true, ELEMENT_SCOPE_ROUTE);
    if (!res.ok) {
      // save failed; user stays on page and sees error
      return;
    }
    // uploadToServer will navigate on success because navigateAfter=true
  };

  const handleSkip = () => navigate(ELEMENT_SCOPE_ROUTE);
  const handleBack = () => navigate(ROUTES.COST_ESTIMATION_STEP5);

  const handleRowFileInput = (idx, e) => {
    const f = e.target.files && e.target.files[0];
    if (f) onRowFileSelected(idx, f);
    e.target.value = null;
  };

  const openFilePicker = (idx) => {
    const el = fileInputRefs.current[idx];
    if (el) el.click();
  };

  // Opens a document that was already uploaded and saved to the server in
  // an earlier save (attached.existing -- see the DB-prefill effect
  // above), via GET /basis_of_cost_api/files. Uses `api` (the shared axios
  // instance from apiConfig, not a plain <a href>) so the JWT is attached
  // automatically. Per request, this is view-only: it fetches the file as
  // a blob and opens it in a new tab with no `download` attribute, so the
  // browser's native viewer renders it inline (PDF preview / image)
  // instead of forcing a Save dialog -- there is deliberately no separate
  // Download button/handler any more.
  const viewExistingBasisFile = async (attached) => {
    if (!attached || !attached.filename || !attached.basisId) return;
    try {
      const res = await api.get(`${API_BASE}/basis_of_cost_api/files`, {
        params: { basis_id: attached.basisId, filename: attached.filename },
        responseType: "blob"
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Not revoked immediately -- the new tab needs the object URL to
      // stay valid while it loads/renders the file. The browser releases
      // it when that tab is closed/navigated away, same as the local
      // (not-yet-saved) "View File" object URLs created in makePreview().
    } catch (err) {
      console.error("Failed to view existing basis of cost file:", err);
      alert("Could not open the file. Make sure the backend was restarted after this endpoint was added.");
    }
  };

  // Per request: once a project has been through the full wizard and
  // Finish has been clicked, every read-only step page for it gets a
  // "Print Report" / "View Dashboard" shortcut in the top-right corner,
  // below the breadcrumb -- no need to jump back to the Estimation
  // Summary page just to print the report or check the portfolio
  // Dashboard. Print Report reuses printProjectReport.js's self-contained
  // generator (the same one ProjectList.jsx's "Estimated" column icon
  // already calls) -- given just a projectId, it fetches everything
  // itself and opens the report exactly the same way.
  const [printReportLoading, setPrintReportLoading] = useState(false);
  const handlePrintReport = async () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    setPrintReportLoading(true);
    try {
      await printProjectReport({ authFetch, API_BASE, projectId });
    } catch (err) {
      console.error("Failed to generate report", err);
    } finally {
      setPrintReportLoading(false);
    }
  };

  return (
    <>
      {/* Was a plain "Start Cost Estimation" title -- replaced with the
          actual project name in a white block, matching every other step
          in this wizard. */}
      <div className="page-title-head d-flex align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail instead of a
              plain bordered/uppercase title: "Cost Estimation / Basis of
              Cost / Project : {name}", each non-current segment (and
              the "/" separators) in #788ca5 at 14px, normal case, no
              underline -- matching the reference design. The current
              segment ("Project : {name}") stays semibold in the app's
              brand teal so it still reads as the active/current item,
              same idea as the reference's blue "Portfolio" segment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {projectName || "—"}
            </span>
          </nav>
        </div>

        {/* Per request: "Print Report" / "View Dashboard" shortcut, top
            right corner below the header, same on every read-only wizard
            step page for a finished project. */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#124d52", borderRadius: 2 }}
            onClick={handlePrintReport}
            disabled={printReportLoading}
          >
            <i className="ti ti-printer fs-5" />
            {printReportLoading ? "Preparing..." : "Print Report"}
          </button>
          <button
            type="button"
            className="btn btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#02bc9c", color: "#fff", borderRadius: 2 }}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <i className="ti ti-dashboard fs-5" />
            View Dashboard
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Wizard progress bar -- see the note above WIZARD_STEPS in
                  Step1ProjectInfo.jsx for why this is a fixed percentage
                  per page instead of the theme's JS-driven one. */}
              <div className="progress mb-4" style={{ height: 6 }}>
                <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
              </div>

              {/* Step indicator -- read-only view: every step is "done" and
                  every tile is clickable, jumping to that step's own
                  read-only view page (see STEP_VIEW_ROUTES / handleStepNav
                  above). */}
              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className="nav-link h-100 wizard-item-done"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleStepNav(step.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleStepNav(step.key);
                        }
                      }}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <hr className="mt-3" />

              <h5 className="mb-3 text-green fs-4 fw-bold">Basis of Cost</h5>

              {showSuccessAlert && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <strong>Success:</strong> {info || "Data inserted successfully"}
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowSuccessAlert(false)} />
                </div>
              )}
              {error && <div className="alert alert-danger">{error}</div>}

              {/* FIX: this whole section used to be wrapped in a single
                  <fieldset disabled>, which is how this page turns the
                  (otherwise-editable, copied-from-BasisOfCost.jsx) form
                  fields into a read-only display -- a disabled <fieldset>
                  disables every form control nested inside it (inputs,
                  textareas, buttons), with NO way to selectively re-enable
                  one of them: nesting another fieldset/button inside a
                  disabled fieldset and giving IT disabled={false} does not
                  un-disable it, per the HTML spec -- disabling always wins
                  from any ancestor fieldset. That's why "View File" below
                  used to render but never actually respond to clicks: it
                  was just as disabled as every other field. Fixed by
                  dropping the fieldset entirely and disabling each field
                  individually instead, which leaves the "View File"
                  button(s) as the only interactive controls in this
                  section. */}
              <div style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <p className="mb-0">
                  <strong>Information Used</strong> - <span className="text-danger">The following information has been used in the production of this cost plan:</span>
                </p>
                <button type="button" className="btn btn-link btn-sm p-0 text-nowrap ms-3" onClick={clearDraft} disabled>
                  Reset
                </button>
              </div>

              {/* One row per drawing -- the theme's own mockup only shows a
                  single static row, but this app supports attaching more
                  than one drawing (addRow/removeRow), so each entry repeats
                  the same theme fields with its own Remove control. */}
              {rows.map((r, idx) => (
                <div className="bg-disabled pt-3 pb-2 mb-2 px-2" key={idx}>
                  {/* FIX: this used to be absolutely positioned
                      (position-absolute end-0 top-0) on top of the whole
                      row, which landed it directly on top of the "Upload
                      (jpg/pdf)" label in the second column -- the two
                      overlapped and "Remove" was unreadable. Rendering it
                      in normal flow, right-aligned, above the row keeps it
                      in the same top-right spot without covering anything. */}
                  {rows.length > 1 && (
                    <div className="d-flex justify-content-end mb-1">
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        onClick={() => removeRow(idx)}
                        title="Remove this drawing"
                        disabled
                      >
                        ✕ Remove
                      </button>
                    </div>
                  )}

                  <div className="row">
                  <div className="col-xl-5">
                    <div className="mb-3">
                      <label className="form-label">Drawing Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Drawing 1"
                        maxLength={DRAWING_NAME_MAX}
                        value={r.drawingName}
                        onChange={(e) => updateRowWithLimit(idx, "drawingName", e.target.value, DRAWING_NAME_MAX)}
                        disabled
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Drawing No.</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Drawing No."
                        maxLength={DRAWING_NO_MAX}
                        value={r.drawingNo}
                        onChange={(e) => updateRowWithLimit(idx, "drawingNo", e.target.value, DRAWING_NO_MAX)}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-xl-5">
                    <div className="mb-3">
                      <label className="form-label">Upload (jpg/pdf)</label>
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className="form-control"
                          readOnly
                          disabled
                          placeholder="Drawing No.pdf"
                          value={r.attached ? r.attached.name : ""}
                          onClick={() => openFilePicker(idx)}
                        />
                        {/* Per request: no separate Upload/Save buttons --
                            clicking the field above is the only upload
                            action needed (see openFilePicker). Once a file
                            is attached, "View File" appears so it can be
                            previewed -- opens the attached document's
                            local object URL (created in makePreview() when
                            the file was selected) in a new tab. Actually
                            persisting the row to the server still happens
                            when "Next" is clicked (uploadToServer, via
                            handleNext below) -- there's no standalone Save
                            button anymore.

                            r.attached.existing (set by the DB-prefill
                            effect above) marks a document that was
                            already uploaded in an earlier save -- there's
                            no local blob for it, so it's fetched from the
                            server on demand instead ("View File", via
                            viewExistingBasisFile) rather than opened
                            straight from r.attached.url. Per request, this
                            is view-only -- no separate Download button. */}
                        {r.attached && (
                          r.attached.existing ? (
                            <button
                              type="button"
                              className="btn btn-outline-secondary text-nowrap"
                              onClick={() => viewExistingBasisFile(r.attached)}
                              title={r.attached.name}
                            >
                              <i className="ti ti-eye me-1" /> View File
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-outline-secondary text-nowrap"
                              onClick={() => window.open(r.attached.url, "_blank", "noopener,noreferrer")}
                              title={r.attached.name}
                            >
                              <i className="ti ti-eye me-1" /> View File
                            </button>
                          )
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        hidden
                        disabled
                        ref={(el) => (fileInputRefs.current[idx] = el)}
                        onChange={(e) => handleRowFileInput(idx, e)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">URL</label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://cet.domain.com"
                        value={r.url}
                        onChange={(e) => updateRow(idx, "url", e.target.value)}
                        disabled
                      />
                    </div>
                  </div>

                  </div>
                </div>
              ))}

              <div className="mb-3">
                {/* FIX: was <a href="javascript:void(0);">, which newer
                    React versions block outright ("React has blocked a
                    javascript: URL as a security precaution"), throwing
                    on every click and breaking Add More entirely. A plain
                    button styled to look like the same link fixes it
                    without changing the click behavior. */}
                <button
                  type="button"
                  className="btn btn-link p-0 text-success text-decoration-none"
                  onClick={addRow}
                  disabled
                >
                  <i className="ti ti-circle-plus" /> Add More
                </button>
              </div>

              {uploading && (
                <div className="progress mb-3" style={{ height: 20 }}>
                  <div className="progress-bar" role="progressbar" style={{ width: `${progress}%` }}>
                    {progress}%
                  </div>
                </div>
              )}

              <div className="mb-3 mt-2">
                <label className="form-label">
                  <strong>Assumptions</strong> - <span className="text-danger fs-xxs">The following list is not exhaustive but identifies a number of key assumptions. Further assumptions may have been made in the production of this cost plan:</span>
                </label>
                <textarea className="form-control" rows="3" value={assumptions} onChange={(e) => setAssumptions(e.target.value)} disabled />
              </div>
              <div className="mb-3">
                <label className="form-label">Exclusions</label>
                <textarea className="form-control" rows="3" value={exclusions} onChange={(e) => setExclusions(e.target.value)} disabled />
              </div>
              </div>

              <div className="d-flex flex-column flex-sm-row justify-content-sm-between gap-2 mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.DASHBOARD, { state: { tab: "executive" } })}>
                  &larr; Back to Dashboard
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.COST_ESTIMATION_SUMMARY)}>
                  &larr; Back to Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}